import craftingTableCatalog from "./crafting-tables.json";
import faceplateCatalog from "./faceplates.json";
import specialItemCatalog from "./special-items.json";
import wallpaperCatalog from "./wallpapers.json";

export type ItemId = string;

export type InventorySlot = {
  slotId: string;
  itemId: ItemId;
  quantity: number;
};

export type Recipe = {
  id: string;
  name: string;
  description: string;
  outputItemId: ItemId;
  outputQuantity: number;
  width: number;
  height: number;
  shaped: boolean;
  grid: Array<ItemId | null>;
  acceptedVariants?: Array<Array<ItemId | null>>;
  difficulty: 1 | 2 | 3 | 4 | 5;
  modes: string[];
};

export type Grid = Array<ItemId | null>;
export type MatchMode = "quick" | "ranked";
export type MatchOutcome = "win" | "loss";

export type CosmeticCategory = "wallpaper" | "crafting-table" | "faceplate";

export type TableColorPalette = {
  surface: string;
  edge: string;
  highlight: string;
  accent: string;
  label: string;
};

export type CosmeticItem = {
  id: string;
  category: CosmeticCategory;
  name: string;
  rarity: "starter" | "uncommon" | "rare" | "epic";
  coinPrice: number;
  assetPath?: string;
  creator?: string;
  license?: string;
  sourceUrl?: string;
  tablePalette?: TableColorPalette;
  slotTexturePath?: string;
};

export type SpecialItem = {
  id: string;
  category: "special";
  name: string;
  description: string;
  rarity: "uncommon" | "rare" | "epic";
  coinPrice: number;
};

export type CosmeticLoadout = {
  wallpaperId: string;
  craftingTableId: string;
  faceplateId: string;
};

export const DEFAULT_COSMETIC_LOADOUT: CosmeticLoadout = {
  wallpaperId: "overworld-day",
  craftingTableId: "classic-brown",
  faceplateId: "trailblazer"
};

const CRAFTING_TABLE_CATALOG = craftingTableCatalog as CosmeticItem[];
const FACEPLATE_CATALOG = faceplateCatalog as CosmeticItem[];
const WALLPAPER_CATALOG = wallpaperCatalog as CosmeticItem[];
export const SPECIAL_ITEM_CATALOG: readonly SpecialItem[] = specialItemCatalog as SpecialItem[];

export const COSMETIC_CATALOG: readonly CosmeticItem[] = [
  ...WALLPAPER_CATALOG,
  ...CRAFTING_TABLE_CATALOG,
  ...FACEPLATE_CATALOG
] as const;

export function cosmeticItem(itemId: string): CosmeticItem | undefined {
  return COSMETIC_CATALOG.find((item) => item.id === itemId);
}

export function specialItem(itemId: string): SpecialItem | undefined {
  return SPECIAL_ITEM_CATALOG.find((item) => item.id === itemId);
}

export function isCosmeticForCategory(itemId: string, category: CosmeticCategory): boolean {
  return COSMETIC_CATALOG.some((item) => item.id === itemId && item.category === category);
}

export function normalizeCosmeticLoadout(loadout?: Partial<CosmeticLoadout> | null): CosmeticLoadout {
  return {
    wallpaperId: loadout?.wallpaperId && isCosmeticForCategory(loadout.wallpaperId, "wallpaper")
      ? loadout.wallpaperId
      : DEFAULT_COSMETIC_LOADOUT.wallpaperId,
    craftingTableId: loadout?.craftingTableId && isCosmeticForCategory(loadout.craftingTableId, "crafting-table")
      ? loadout.craftingTableId
      : DEFAULT_COSMETIC_LOADOUT.craftingTableId,
    faceplateId: loadout?.faceplateId && isCosmeticForCategory(loadout.faceplateId, "faceplate")
      ? loadout.faceplateId
      : DEFAULT_COSMETIC_LOADOUT.faceplateId
  };
}

export function equipCosmetic(loadout: CosmeticLoadout, itemId: string): CosmeticLoadout {
  const item = cosmeticItem(itemId);
  if (!item) return normalizeCosmeticLoadout(loadout);
  if (item.category === "wallpaper") return { ...loadout, wallpaperId: item.id };
  if (item.category === "crafting-table") return { ...loadout, craftingTableId: item.id };
  return { ...loadout, faceplateId: item.id };
}

export type XpAdjustment = {
  previousXp: number;
  rivalXp: number;
  nextXp: number;
  delta: number;
};

export type InventoryGenerationOptions = {
  itemFamily?: (itemId: ItemId) => string;
  variantSeed?: number;
  round?: number;
};

// A match is best-of-five: the fifth round is only needed when the score reaches 2–2.
export const ROUNDS_TO_WIN = 3;
export const MATCH_INTRO_SEQUENCE_MS = 2_000;
export const ROUND_START_SEQUENCE_MS = 6_000;
export const MATCH_START_SEQUENCE_MS = MATCH_INTRO_SEQUENCE_MS + ROUND_START_SEQUENCE_MS;
export const RANKED_UNLOCK_XP = 1_000;
export const MATCH_XP_K_FACTOR = 128;
export const XP_REWARD_COINS = 250;
export const XP_FIRST_MILESTONE = 1_000;
export const XP_SECOND_MILESTONE = 5_000;
export const XP_LATER_INTERVAL = 7_000;
export const MASTER_CRAFT_REWARD_COINS = 5;
export const MASTER_CRAFT_DAILY_LIMIT = 5;
export const MASTER_CRAFT_LATENCY_ADJUSTMENT_MS = 200;
export const MASTER_CRAFT_DIFFICULTY_4_MS = 5_000;
export const MASTER_CRAFT_DIFFICULTY_5_MS = 7_000;
export const REWARDED_AD_COINS = 30;
export const REWARDED_AD_DAILY_LIMIT = 3;

export function xpMilestone(index: number): number {
  const normalized = Math.max(0, Math.floor(index));
  if (normalized === 0) return XP_FIRST_MILESTONE;
  if (normalized === 1) return XP_SECOND_MILESTONE;
  return XP_SECOND_MILESTONE + XP_LATER_INTERVAL * (normalized - 1);
}

export function xpMilestonesThrough(xp: number): number[] {
  const normalized = Math.max(0, Math.floor(xp));
  if (normalized < XP_FIRST_MILESTONE) return [];
  const milestones = [XP_FIRST_MILESTONE];
  if (normalized < XP_SECOND_MILESTONE) return milestones;
  milestones.push(XP_SECOND_MILESTONE);
  for (let threshold = XP_SECOND_MILESTONE + XP_LATER_INTERVAL; threshold <= normalized; threshold += XP_LATER_INTERVAL) {
    milestones.push(threshold);
  }
  return milestones;
}

export function nextXpCoinMilestone(xp: number): number {
  const normalized = Math.max(0, Math.floor(xp));
  if (normalized < XP_FIRST_MILESTONE) return XP_FIRST_MILESTONE;
  if (normalized < XP_SECOND_MILESTONE) return XP_SECOND_MILESTONE;
  return XP_SECOND_MILESTONE + XP_LATER_INTERVAL * (Math.floor((normalized - XP_SECOND_MILESTONE) / XP_LATER_INTERVAL) + 1);
}

export type PlayerLevelProgress = {
  level: number;
  xp: number;
  levelStartXp: number;
  nextLevelXp: number;
  xpIntoLevel: number;
  xpRequiredForLevel: number;
  xpRemaining: number;
  progress: number;
};

export function playerLevelProgress(xp: number): PlayerLevelProgress {
  const normalized = Math.max(0, Math.floor(xp));
  let level: number;
  let levelStartXp: number;
  let nextLevelXp: number;
  if (normalized < XP_FIRST_MILESTONE) {
    level = 1;
    levelStartXp = 0;
    nextLevelXp = XP_FIRST_MILESTONE;
  } else if (normalized < XP_SECOND_MILESTONE) {
    level = 2;
    levelStartXp = XP_FIRST_MILESTONE;
    nextLevelXp = XP_SECOND_MILESTONE;
  } else {
    level = 3 + Math.floor((normalized - XP_SECOND_MILESTONE) / XP_LATER_INTERVAL);
    levelStartXp = XP_SECOND_MILESTONE + (level - 3) * XP_LATER_INTERVAL;
    nextLevelXp = levelStartXp + XP_LATER_INTERVAL;
  }
  const xpRequiredForLevel = nextLevelXp - levelStartXp;
  const xpIntoLevel = normalized - levelStartXp;
  return {
    level,
    xp: normalized,
    levelStartXp,
    nextLevelXp,
    xpIntoLevel,
    xpRequiredForLevel,
    xpRemaining: nextLevelXp - normalized,
    progress: xpRequiredForLevel > 0 ? xpIntoLevel / xpRequiredForLevel : 0
  };
}

export function masterCraftThresholdMs(difficulty: Recipe["difficulty"]): number | null {
  if (difficulty === 4) return MASTER_CRAFT_DIFFICULTY_4_MS;
  if (difficulty === 5) return MASTER_CRAFT_DIFFICULTY_5_MS;
  return null;
}

export function calculateMatchXp(currentXp: number, rivalXp: number, outcome: MatchOutcome): XpAdjustment {
  const previousXp = Math.max(0, Math.floor(currentXp));
  const normalizedRivalXp = Math.max(0, Math.floor(rivalXp));
  const [unclampedNextXp] = calculateElo(previousXp, normalizedRivalXp, outcome === "win" ? 1 : 0, MATCH_XP_K_FACTOR);
  const nextXp = Math.max(0, unclampedNextXp);
  return { previousXp, rivalXp: normalizedRivalXp, nextXp, delta: nextXp - previousXp };
}

export function selectRivalXp(currentXp: number, matchSeed: number, mode: MatchMode): number {
  const playerXp = Math.max(0, Math.floor(currentXp));
  const range = mode === "ranked" ? 100 : 200;
  const random = createSeededRandom(hashSeed(`${matchSeed}:${mode}:rival-xp`));
  const offset = Math.round(((random() * 2 - 1) * range) / 25) * 25;
  return Math.max(0, playerXp + offset);
}

export function isRankedUnlocked(xp: number): boolean {
  return xp >= RANKED_UNLOCK_XP;
}

export function hashSeed(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffleSeeded<T>(values: readonly T[], seed: number): T[] {
  const output = [...values];
  const random = createSeededRandom(seed);
  for (let index = output.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [output[index], output[other]] = [output[other]!, output[index]!];
  }
  return output;
}

export function selectRoundValue<T>(values: readonly T[], matchSeed: number, round: number): T {
  if (!values.length) throw new Error("Cannot select a round value from an empty list.");
  const index = Math.max(0, round - 1) % values.length;
  return shuffleSeeded(values, matchSeed)[index]!;
}

export function selectRoundValueByFamily<T>(
  values: readonly T[],
  matchSeed: number,
  round: number,
  familyForValue: (value: T) => string,
  consecutiveGroupForValue?: (value: T) => string
): T {
  if (!values.length) throw new Error("Cannot select a round value from an empty list.");

  const valuesByFamily = new Map<string, T[]>();
  for (const value of values) {
    const family = familyForValue(value);
    const familyValues = valuesByFamily.get(family) ?? [];
    familyValues.push(value);
    valuesByFamily.set(family, familyValues);
  }

  const families = shuffleSeeded([...valuesByFamily.keys()], matchSeed);
  const roundIndex = Math.max(0, round - 1);
  const familyCycle = Math.floor(roundIndex / families.length);
  if (!consecutiveGroupForValue) {
    const family = families[roundIndex % families.length]!;
    const familyValues = valuesByFamily.get(family)!;
    const variants = shuffleSeeded(familyValues, hashSeed(`${matchSeed}:${family}:variants`));
    return variants[familyCycle % variants.length]!;
  }

  let previousGroup: string | null = null;
  let orderedCycle: T[] = [];
  for (let cycle = 0; cycle <= familyCycle; cycle += 1) {
    const candidates = families.map((family) => {
      const variants = shuffleSeeded(valuesByFamily.get(family)!, hashSeed(`${matchSeed}:${family}:variants`));
      return variants[cycle % variants.length]!;
    });
    orderedCycle = orderAvoidingConsecutiveGroups(candidates, consecutiveGroupForValue, previousGroup);
    previousGroup = consecutiveGroupForValue(orderedCycle.at(-1)!);
  }
  return orderedCycle[roundIndex % families.length]!;
}

function orderAvoidingConsecutiveGroups<T>(
  values: readonly T[],
  groupForValue: (value: T) => string,
  previousGroup: string | null
): T[] {
  const valuesByGroup = new Map<string, T[]>();
  for (const value of values) {
    const group = groupForValue(value);
    const groupValues = valuesByGroup.get(group) ?? [];
    groupValues.push(value);
    valuesByGroup.set(group, groupValues);
  }

  const groupOrder = [...valuesByGroup.keys()];
  const groupIndexes = new Map(groupOrder.map((group) => [group, 0]));
  const output: T[] = [];
  let lastGroup = previousGroup;
  while (output.length < values.length) {
    let selectedGroup: string | null = null;
    for (const group of groupOrder) {
      const remaining = valuesByGroup.get(group)!.length - groupIndexes.get(group)!;
      if (group === lastGroup || remaining === 0) continue;
      const selectedRemaining = selectedGroup === null
        ? 0
        : valuesByGroup.get(selectedGroup)!.length - groupIndexes.get(selectedGroup)!;
      if (selectedGroup === null || remaining > selectedRemaining) {
        selectedGroup = group;
      }
    }
    if (selectedGroup === null) {
      throw new Error("Cannot order round values without repeating a consecutive group.");
    }
    const selectedIndex = groupIndexes.get(selectedGroup)!;
    output.push(valuesByGroup.get(selectedGroup)![selectedIndex]!);
    groupIndexes.set(selectedGroup, selectedIndex + 1);
    lastGroup = selectedGroup;
  }
  return output;
}

export function normalizeRecipeGrid(recipe: Recipe): Grid {
  const output: Grid = Array.from({ length: 9 }, () => null);
  const offsetX = Math.floor((3 - recipe.width) / 2);
  const offsetY = Math.floor((3 - recipe.height) / 2);
  recipe.grid.forEach((item, index) => {
    const x = index % recipe.width;
    const y = Math.floor(index / recipe.width);
    output[(y + offsetY) * 3 + x + offsetX] = item;
  });
  return output;
}

export function recipeShapeHint(recipe: Recipe): boolean[] {
  return normalizeRecipeGrid(recipe).map((item) => item !== null);
}

export function recipeMatches(grid: Grid, recipe: Recipe): boolean {
  if (!recipe.shaped) {
    const actual = grid.filter((item): item is ItemId => item !== null).sort();
    const expected = recipe.grid.filter((item): item is ItemId => item !== null).sort();
    return actual.length === expected.length && actual.every((item, index) => item === expected[index]);
  }

  const matchesAt = (offsetX: number, offsetY: number, mirrored: boolean): boolean => {
    for (let y = 0; y < 3; y += 1) {
      for (let x = 0; x < 3; x += 1) {
        const recipeX = x - offsetX;
        const recipeY = y - offsetY;
        const insideRecipe = recipeX >= 0 && recipeX < recipe.width && recipeY >= 0 && recipeY < recipe.height;
        const sourceX = mirrored ? recipe.width - 1 - recipeX : recipeX;
        const expected = insideRecipe ? (recipe.grid[recipeY * recipe.width + sourceX] ?? null) : null;
        if (grid[y * 3 + x] !== expected) return false;
      }
    }
    return true;
  };

  for (let offsetY = 0; offsetY <= 3 - recipe.height; offsetY += 1) {
    for (let offsetX = 0; offsetX <= 3 - recipe.width; offsetX += 1) {
      if (matchesAt(offsetX, offsetY, false) || matchesAt(offsetX, offsetY, true)) return true;
    }
  }

  return (recipe.acceptedVariants ?? []).some((variant) => variant.length === 9 && variant.every((item, index) => item === grid[index]));
}

export function ingredientCounts(recipe: Recipe): Map<ItemId, number> {
  const counts = new Map<ItemId, number>();
  for (const item of recipe.grid) {
    if (item) counts.set(item, (counts.get(item) ?? 0) + 1);
  }
  return counts;
}

export function generateInventory(
  recipe: Recipe,
  decoys: readonly ItemId[],
  seed: number,
  options: InventoryGenerationOptions = {}
): InventorySlot[] {
  const ingredients = [...ingredientCounts(recipe)].map(([itemId, quantity]) => ({ itemId, quantity }));
  const itemFamily = options.itemFamily ?? ((itemId: ItemId) => itemId);
  const neededFamilies = new Set(ingredients.map(({ itemId }) => itemFamily(itemId)));
  const decoysByFamily = new Map<string, ItemId[]>();

  for (const itemId of decoys) {
    const family = itemFamily(itemId);
    if (neededFamilies.has(family)) continue;
    const variants = decoysByFamily.get(family) ?? [];
    if (!variants.includes(itemId)) variants.push(itemId);
    decoysByFamily.set(family, variants);
  }

  const variantSeed = options.variantSeed ?? seed;
  const round = Math.max(1, Math.floor(options.round ?? 1));
  const representatives = [...decoysByFamily].map(([family, variants]) =>
    selectRoundValue(variants, hashSeed(`${variantSeed}:${family}:inventory-variants`), round)
  );
  const additions = shuffleSeeded(representatives, seed).slice(0, Math.max(0, 10 - ingredients.length)).map((itemId) => ({
    itemId,
    quantity: 1 + (hashSeed(`${seed}:${itemId}`) % 2)
  }));
  return shuffleSeeded([...ingredients, ...additions], seed + 17).map((slot, index) => ({
    slotId: `inventory-${index}`,
    ...slot
  }));
}

export function placeItem(grid: Grid, slot: number, itemId: ItemId): Grid {
  if (slot < 0 || slot >= 9) return grid;
  const next = [...grid];
  next[slot] = itemId;
  return next;
}

export function clearItem(grid: Grid, slot: number): Grid {
  if (slot < 0 || slot >= 9) return grid;
  const next = [...grid];
  next[slot] = null;
  return next;
}

export function availableQuantity(inventory: InventorySlot[], grid: Grid, itemId: ItemId): number {
  const total = inventory.filter((slot) => slot.itemId === itemId).reduce((sum, slot) => sum + slot.quantity, 0);
  return Math.max(0, total - grid.filter((item) => item === itemId).length);
}

export function calculateElo(ratingA: number, ratingB: number, scoreA: 0 | 0.5 | 1, k = 32): [number, number] {
  const expectedA = 1 / (1 + 10 ** ((ratingB - ratingA) / 400));
  const delta = Math.round(k * (scoreA - expectedA));
  return [ratingA + delta, ratingB - delta];
}
